/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import cstmTable from "../../../../share/components/vue/PaginationTable";
import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';


/**
 * 各フェーズ定義
 */
const PHASE = {
  START: 'start',
  EXECUTING: 'executing',
  SUCCESS: 'success',
  EXECUTE_FAILED: 'executeFailed',
  FAILED: 'failed'
};

export default {
  name: 'requirement105',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      bodyItems: [],
      isErrorCheck: true,
      modal_requirement_105: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },
  components: {
    "cstm-table": cstmTable
  },


  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    showTable() {
      return this.phase === "executing" || this.phase === "success" || this.phase === "executeFailed";
    },

    tableColumns() {
      if (!this.showTable) return [];
      const columnMap = {
        executing: [
          {
            name: "itemName1",
            label: `${this.i18nWsKey}.REQ_WS_105_TXT_2_0001`
          }
        ],
        success: [
          {
            name: "itemName1",
            label: `${this.i18nWsKey}.REQ_WS_105_TXT_4_0002`
          }
        ],
        executeFailed: [
          {
            name: "itemName1",
            label: `${this.i18nWsKey}.REQ_WS_105_TXT_6`
          }
        ]
      };
      return columnMap[this.phase] ?? [];
    },

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.EXECUTE_FAILED:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTING:
        default:
          return 'secondary';
      }
    },

    headerTitle() {
      // 待機
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行監視
      const ExecutionTitle = 'individual.common_title_execution';
      // 停止(正常終了)
      const SuccessTitle = 'message.common_title_success';
      // エラー(クリア失敗)、共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ConfirmationTitle,
        executing: ExecutionTitle,
        success: SuccessTitle,
        executeFailed: ErrorTitle,
        failed: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    workSupportMessage() {
      const messageMap = {
        start: `${this.i18nWsKey}.REQ_WS_105_TXT_1`,
        executing: `${this.i18nWsKey}.REQ_WS_105_TXT_3_0001`,
        success: `${this.i18nWsKey}.REQ_WS_105_TXT_5_0002`,
        executeFailed: `${this.i18nWsKey}.REQ_WS_105_TXT_7`,
        failed: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },


    showButtonOk() {
      const showOkButtonMap = {
        start: false,
        executing: false,
        success: true,
        executeFailed: false,
        failed: true,
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  async mounted() {
    // 初期化処理を呼び出す
    await this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      // EUCキーの取得
      this.modal_requirement_105 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    //----- htmlイベント処理 -----------------------------------------------//

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_105 = false;
    },

    /** 画面を閉じる（停止処理を実行する）ボタンクリックイベント */
    async clickStop() {
      // 終了判定結果の取得
      if (await this.stopPostGet()) {
        this.closeWizard();
      }
    },

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      // 終了判定結果の取得
      if (await this.exitPostGet()) {
        this.closeWizard();
      }
    },

    /** 待機画面OKボタンクリックイベント */
    async executeProcess() {
      // 実行監視画面を表示
      await this.showExecuting();
      // ルーチン実行処理を呼び出す
      await this.executePostGet();
    },

    //----- PHASE操作処理 -----------------------------------------------//

    /** 実行監視表示処理 */
    showExecuting() {
      this.phase = PHASE.EXECUTING;
    },

    /** 停止表示処理 */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** エラー表示処理 */
    showError() {
      this.phase = PHASE.EXECUTE_FAILED;
    },

    /** 共通エラー表示処理 */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行処理 -----------------------------------------------//

    /**
   * API 通信で共通バリデーションでエラーが発生した際のコールバック
   * @param {object} result バリデーション結果
   */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; if (this.isErrorCheck) this.showUnexpectedError(); },
      );
    },

    /**
     * ルーチン実行処理
     */
    async executePostGet() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        // 正常コールバック
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 正常終了
            switch (response.data.user_data.result) {
              // 停止画面に遷移する。
              case 'ok':
                await this.showSuccess();
                break;
              case 'ng':
              default:
                await this.showError();
                break;
            }
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            return;
        }
      } finally {
        this.isLoading = false;
      }
    },
    /**
   * ルーチン実行処理(停止 or エラー)
   */
    async stopPostGet() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (!response) return;

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            await this.exitPostGet();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 終了処理
     * 
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 終了処理のエラー判定を行わない場合
        if (!this.isErrorCheck) {
          this.closeWizard();
          return;
        }

        if (!response) return;

        // 終了処理のエラー判定を行う場合
        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            await this.closeWizard();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // 異常終了：共通エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    onOkClicked() {
      switch (this.phase) {
        case 'success':
          this.clickStop();
          break;
        case 'failed':
          this.clickClose();
          break;
        default:
          break;
      }
    },

    onRunClicked() {
      if (this.phase === 'start') {
        this.executeProcess();
      }
    },

    onStopClicked() {
      if (this.phase === 'executeFailed') {
        this.clickStop();
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 終了処理で再度共通エラーが表示されないようにする
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.showFailed();
    },
  }
};